import React, { useState, useEffect } from 'react'
import './AdminUI.css'
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';

const modalBoxSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  maxWidth: '90%',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: 1
};

export default function AdminUI() {
  const outletContext = useOutletContext();
  const navOpen = outletContext?.navOpen ?? true; // Default to true if context not available
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    if (!currentUser || !currentUser.accessToken) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/users/list-all-users`,
        { headers: { 'AuthToken': currentUser.accessToken } }
      );
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users. Please try again.');
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      toast.error('Please enter both email and password');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Password length validation
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/users/create-user`,
        { email: newUserEmail, password: newUserPassword },
        { headers: { 'AuthToken': currentUser.accessToken } }
      );

      toast.success('User created successfully');
      setCreateModalOpen(false);
      setNewUserEmail('');
      setNewUserPassword('');

      // Refresh the user list
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setNewUserEmail('');
    setNewUserPassword('');
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditDisplayName(user.displayName || '');
    setEditEmail(user.email || '');
    setEditPhoneNumber(user.phoneNumber || '');
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
    setEditDisplayName('');
    setEditEmail('');
    setEditPhoneNumber('');
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser?.uid) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!emailRegex.test(editEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSavingEdit(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/users/update-user`,
        {
          uid: editingUser.uid,
          displayName: editDisplayName,
          email: editEmail.trim(),
          phoneNumber: editPhoneNumber,
        },
        { headers: { 'AuthToken': currentUser.accessToken } }
      );

      toast.success('User updated successfully');
      handleCloseEditModal();
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSavingEdit(false);
    }
  };

  const ROLE_LABELS = { admin: 'Admin', dancerAdmin: 'Dancer Admin' };

  const handleRoleChange = async (uid, newRole) => {
    // Store the original user data for rollback
    const originalUser = users.find(u => u.uid === uid);

    // Optimistically update the UI
    setUsers(prevUsers =>
      prevUsers.map(user => {
        if (user.uid === uid) {
          const updatedClaims = { ...(user.customClaims || {}) };
          if (newRole) {
            updatedClaims.role = newRole;
          } else {
            delete updatedClaims.role;
          }
          return { ...user, customClaims: updatedClaims };
        }
        return user;
      })
    );

    try {
      await axios.post(
        `${process.env.REACT_APP_API}/users/update-admin-role`,
        { uid, role: newRole || null },
        { headers: { 'AuthToken': currentUser.accessToken } }
      );

      toast.success(newRole ? `${ROLE_LABELS[newRole]} role granted` : 'Role removed');

      // Refresh the user list to get the latest data from Firebase
      fetchUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Failed to update role. Please try again.');

      // Revert the optimistic update on error
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.uid === uid ? originalUser : user
        )
      );
    }
  };

  const handleRotaEligibleChange = async (uid, rotaEligible) => {
    const originalUser = users.find(u => u.uid === uid);

    setUsers(prevUsers =>
      prevUsers.map(user => {
        if (user.uid === uid) {
          const updatedClaims = { ...(user.customClaims || {}) };
          if (rotaEligible) {
            updatedClaims.rotaEligible = true;
          } else {
            delete updatedClaims.rotaEligible;
          }
          return { ...user, customClaims: updatedClaims };
        }
        return user;
      })
    );

    try {
      await axios.post(
        `${process.env.REACT_APP_API}/users/update-rota-eligible`,
        { uid, rotaEligible },
        { headers: { 'AuthToken': currentUser.accessToken } }
      );

      toast.success(rotaEligible ? 'Rota eligibility granted' : 'Rota eligibility removed');
      fetchUsers();
    } catch (error) {
      console.error('Failed to update rota eligibility:', error);
      toast.error('Failed to update rota eligibility. Please try again.');
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.uid === uid ? originalUser : user
        )
      );
    }
  };

  return (
    <div className={navOpen ? 'outlet' : 'outlet-expanded'} style={{ padding: '2rem' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Firebase Auth Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Users: {users.length}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setCreateModalOpen(true)}
          sx={{ backgroundColor: '#395076' }}
        >
          Create New User
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell><strong>UID</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Display Name</strong></TableCell>
                <TableCell><strong>Phone Number</strong></TableCell>
                <TableCell><strong>Email Verified</strong></TableCell>
                <TableCell><strong>Disabled</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Rota</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Last Sign In</strong></TableCell>
                <TableCell><strong>Edit</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const userRole = user.customClaims?.role || '';
                  const rotaEligible = user.customClaims?.rotaEligible === true;
                  return (
                    <TableRow key={user.uid} hover>
                      <TableCell>{user.uid}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>{user.displayName || 'N/A'}</TableCell>
                      <TableCell>{user.phoneNumber || 'N/A'}</TableCell>
                      <TableCell>{user.emailVerified ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{user.disabled ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Select
                          value={userRole}
                          onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                          size="small"
                          displayEmpty
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="dancerAdmin">Dancer Admin</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={rotaEligible}
                          onChange={(e) => handleRotaEligibleChange(user.uid, e.target.checked)}
                          size="small"
                          inputProps={{ 'aria-label': 'Rota eligible' }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.metadata?.creationTime)}</TableCell>
                      <TableCell>{formatDate(user.metadata?.lastSignInTime)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label="Edit user"
                          onClick={() => openEditModal(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create User Modal */}
      <Modal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        aria-labelledby="create-user-modal-title"
        aria-describedby="create-user-modal-description"
      >
        <Box sx={modalBoxSx}>
          <Typography id="create-user-modal-title" variant="h6" component="h2" gutterBottom>
            Create New User
          </Typography>
          <TextField
            sx={{ my: 1 }}
            fullWidth
            variant="outlined"
            label="Email"
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            disabled={creating}
          />
          <TextField
            sx={{ my: 1 }}
            fullWidth
            variant="outlined"
            label="Password"
            type="password"
            value={newUserPassword}
            onChange={(e) => setNewUserPassword(e.target.value)}
            disabled={creating}
            helperText="Password must be at least 6 characters"
          />
          <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseCreateModal}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={creating}
              sx={{ backgroundColor: '#395076' }}
            >
              {creating ? 'Creating...' : 'Create User'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        aria-labelledby="edit-user-modal-title"
        aria-describedby="edit-user-modal-description"
      >
        <Box sx={modalBoxSx}>
          <Typography id="edit-user-modal-title" variant="h6" component="h2" gutterBottom>
            Edit User
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {editingUser?.uid}
          </Typography>
          <TextField
            sx={{ my: 1 }}
            fullWidth
            variant="outlined"
            label="Display Name"
            value={editDisplayName}
            onChange={(e) => setEditDisplayName(e.target.value)}
            disabled={savingEdit}
          />
          <TextField
            sx={{ my: 1 }}
            fullWidth
            variant="outlined"
            label="Email"
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            disabled={savingEdit}
          />
          <TextField
            sx={{ my: 1 }}
            fullWidth
            variant="outlined"
            label="Phone Number"
            value={editPhoneNumber}
            onChange={(e) => setEditPhoneNumber(e.target.value)}
            disabled={savingEdit}
            helperText="UK numbers accepted (e.g. 07... or +447...). Leave blank to clear."
          />
          <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseEditModal}
              disabled={savingEdit}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveUserEdit}
              disabled={savingEdit}
              sx={{ backgroundColor: '#395076' }}
            >
              {savingEdit ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  )
}
